(() => {
  const seenPromotions = new WeakSet();
  let lastCartState = null;
  let observedRebuyCart = null;
  let rebuyCartStateObserver = null;

  const deviceType = () => {
    if (window.matchMedia('(max-width: 47.99rem)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 63.99rem)').matches) return 'tablet';
    return 'desktop';
  };

  const detailsFor = (element) => ({
    campaign_id: element.dataset.gwpCampaign,
    qualification_mode: element.dataset.gwpQualificationMode,
    treatment: element.dataset.gwpTreatment,
    image_variant: element.dataset.gwpImage,
    surface: element.dataset.gwpSurface,
    qualification_state: element.dataset.gwpState,
    cart_value: Number(element.dataset.gwpSubtotal || 0) / 100,
    qualifying_threshold: Number(element.dataset.gwpThreshold || 0) / 100,
    gift_in_cart: element.dataset.gwpGiftInCart === 'true',
    gift_quantity: Number(element.dataset.gwpGiftQuantity || 0),
    cart_has_subscription: element.dataset.gwpHasSubscription === 'true',
    device_type: deviceType(),
  });

  const emit = (eventName, detail) => {
    const payload = { event: eventName, ...detail };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(
      new CustomEvent(`wellious:${eventName}`, { detail: payload })
    );
  };

  const observeImpression = (element) => {
    if (seenPromotions.has(element)) return;
    seenPromotions.add(element);

    const report = () => {
      const detail = detailsFor(element);
      emit('gwp_promotion_viewed', detail);
      if (detail.surface === 'cart' || detail.surface === 'rebuy-cart') {
        emit('gwp_progress_viewed', detail);
      }
    };

    if (!('IntersectionObserver' in window)) {
      report();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        report();
      },
      { threshold: 0.5 }
    );
    observer.observe(element);
  };

  const reportCartTransition = (cartPromotion) => {
    if (!cartPromotion) return;
    const next = detailsFor(cartPromotion);

    if (lastCartState) {
      const wasQualified = lastCartState.qualification_state.startsWith('qualified');
      const isQualified = next.qualification_state.startsWith('qualified');

      if (!wasQualified && isQualified) {
        emit('gwp_qualification_reached', next);
        emit(
          next.qualification_mode === 'cart_threshold'
            ? 'gwp_threshold_reached'
            : 'gwp_subscription_qualified',
          next
        );
      }
      if (!lastCartState.gift_in_cart && next.gift_in_cart) {
        emit('gwp_gift_added', next);
      }
      if (lastCartState.gift_in_cart && !next.gift_in_cart) {
        emit('gwp_gift_removed', next);
      }
    }

    lastCartState = next;
  };

  const clonePromotionForRebuy = (source) => {
    const clone = source.cloneNode(true);
    clone.hidden = false;
    clone.dataset.gwpSurface = 'rebuy-cart';
    clone.dataset.gwpRebuyClone = '';
    clone.classList.add('gwp-promotion--rebuy');
    return clone;
  };

  const observeRebuyCartState = (rebuyCart) => {
    if (!rebuyCart || observedRebuyCart === rebuyCart) return;
    rebuyCartStateObserver?.disconnect();
    observedRebuyCart = rebuyCart;
    rebuyCartStateObserver = new MutationObserver(queueScan);
    rebuyCartStateObserver.observe(rebuyCart, {
      attributes: true,
      attributeFilter: ['class'],
    });
  };

  const syncRebuyPromotion = () => {
    const rebuyCart = document.querySelector('#rebuy-cart');
    const rebuyBody = document.querySelector(
      '#rebuy-cart [data-rebuy-cart-anchor="body"]'
    );
    const source = document.querySelector(
      '[data-gwp-promotion][data-gwp-surface="cart"]:not([data-gwp-rebuy-clone])'
    );
    const existing = rebuyBody?.querySelector('[data-gwp-rebuy-clone]');
    const rebuyHasItems =
      rebuyCart?.classList.contains('has-items') &&
      !rebuyCart.classList.contains('no-items');
    const rebuyHasSubscription = [
      ...(rebuyCart?.querySelectorAll(
        '.rebuy-cart__flyout-item-subscription select'
      ) || []),
    ].some((select) => {
      const selectedOption = select.options[select.selectedIndex];
      return /subscription/i.test(selectedOption?.textContent || '');
    });

    observeRebuyCartState(rebuyCart);

    if (!rebuyBody || !source || !rebuyHasItems || !rebuyHasSubscription) {
      existing?.remove();
      return;
    }

    const stateChanged =
      existing &&
      [
        'gwpCampaign',
        'gwpQualificationMode',
        'gwpTreatment',
        'gwpImage',
        'gwpState',
        'gwpSubtotal',
        'gwpThreshold',
        'gwpGiftInCart',
        'gwpGiftQuantity',
        'gwpHasSubscription',
      ].some((key) => existing.dataset[key] !== source.dataset[key]);
    const contentChanged = existing && existing.innerHTML !== source.innerHTML;

    if (!existing) {
      rebuyBody.prepend(clonePromotionForRebuy(source));
    } else if (stateChanged || contentChanged) {
      existing.replaceWith(clonePromotionForRebuy(source));
    }
  };

  const scan = () => {
    syncRebuyPromotion();
    const promotions = [...document.querySelectorAll('[data-gwp-promotion]')];
    promotions.forEach(observeImpression);
    reportCartTransition(
      promotions.find((element) => element.dataset.gwpSurface === 'cart')
    );
  };

  let scanQueued = false;
  const queueScan = () => {
    if (scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(() => {
      scanQueued = false;
      scan();
    });
  };

  document.addEventListener('DOMContentLoaded', scan);
  window.addEventListener('shapes:cart:afteradditem', queueScan);
  window.addEventListener('shapes:cart:cartqtychange', queueScan);
  window.addEventListener('shapes:cart:update', queueScan);
  document.addEventListener('shopify:section:load', queueScan);
  document.addEventListener(
    'change',
    (event) => {
      if (
        event.target.matches(
          '#rebuy-cart .rebuy-cart__flyout-item-subscription select'
        )
      ) {
        queueScan();
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const checkoutControl = event.target.closest(
        '[name="checkout"], .shopify-payment-button__button, .rebuy-cart__checkout-button'
      );
      if (!checkoutControl) return;
      const promotion =
        checkoutControl
          .closest('#rebuy-cart')
          ?.querySelector('[data-gwp-rebuy-clone]') ||
        document.querySelector(
          '[data-gwp-promotion][data-gwp-surface="cart"]'
        );
      if (promotion) emit('gwp_checkout_started', detailsFor(promotion));
    },
    true
  );

  const mutationObserver = new MutationObserver(queueScan);
  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  scan();
})();
